import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { DeleteOutlined } from '@ant-design/icons'
import { Button, Form, Popconfirm, Space, Typography } from 'antd'
import { find, includes, isEmpty, isNil } from 'lodash'
import PropTypes from 'prop-types'

import { getFilterOptions } from '../../../core/utils'

import {
  FormCheckbox,
  FormRichTextHighlightingInput,
} from '../../../common/components/sections'
import { StyledVerticalSpace } from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import { useFormDirty } from '../../../common/hooks'

import { useUserHasPermissions } from '../../../account/hooks'
import { LABEL_LEGEND_MATCH_TYPE } from '../../../menu-curation-tasks/constants'
import {
  UNKNOWN_WORD_INGREDIENT_KEYWORD_MATCH_TYPE,
  UNKNOWN_WORD_TYPE,
} from '../../constants'
import { unknownWordPropType } from '../../prop-types'
import { transformUnknownWordToCardDetails } from '../../transformers'
import {
  StyledUnknownWordsFormSelect,
  StyledUnknownWordsFormTreeSelect,
  StyledUnknownWordsMatchTypeText,
  StyledUnknownWordsTranslationText,
} from '../styled'

import { unknownWordFormInitialValues } from './initial-values'

const UnknownWordForm = ({
  unknownWord,
  isDisabled,
  onDelete,
  onConfirm,
  onUnconfirm,
  isUnknownWordsCurationDone,
  isUnknownWordsQaDone,
}) => {
  const [selectedType, setSelectedType] = useState(null)

  const hasPermissionToDelete = useUserHasPermissions(
    PERMISSIONS.UNKNOWN_WORD.DELETE,
  )

  const { allergenOptions, dietOptions, ingredientOptions, languageOptions } =
    useSelector(store => store.common.metadata.data)

  const unknownWordCardDetails = useMemo(
    () => transformUnknownWordToCardDetails(unknownWord),
    [unknownWord],
  )

  const { translation, labelLegendMatchType } = unknownWordCardDetails

  const initialValues = useMemo(
    () => unknownWordFormInitialValues(unknownWordCardDetails),
    [unknownWordCardDetails],
  )

  useEffect(() => {
    setSelectedType(initialValues.type)
  }, [initialValues])

  const [form] = Form.useForm()

  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const initialLanguage = useMemo(
    () => find(languageOptions, lang => lang.id === initialValues.languageId),
    [initialValues.languageId, languageOptions],
  )

  const unknownWordTypeOptions = useMemo(
    () => getFilterOptions(UNKNOWN_WORD_TYPE, 'label', 'value'),
    [],
  )

  const ingredientKeywordMatchTypeOptions = useMemo(
    () =>
      getFilterOptions(
        UNKNOWN_WORD_INGREDIENT_KEYWORD_MATCH_TYPE,
        'label',
        'value',
      ),
    [],
  )

  const isLanguageSelectShown = useMemo(
    () =>
      includes(
        [
          UNKNOWN_WORD_TYPE.NEW_INGREDIENT.value,
          UNKNOWN_WORD_TYPE.EXISTING_INGREDIENT.value,
          UNKNOWN_WORD_TYPE.COMMON_FOREIGN_WORD.value,
        ],
        selectedType,
      ),
    [selectedType],
  )

  const isIngredientSelectsShown = useMemo(
    () =>
      includes(
        [
          UNKNOWN_WORD_TYPE.NEW_INGREDIENT.value,
          UNKNOWN_WORD_TYPE.EXISTING_INGREDIENT.value,
        ],
        selectedType,
      ),
    [selectedType],
  )

  const isIngredientMatchTypeShown = useMemo(
    () =>
      includes(
        [
          UNKNOWN_WORD_TYPE.NEW_INGREDIENT.value,
          UNKNOWN_WORD_TYPE.EXISTING_INGREDIENT.value,
        ],
        selectedType,
      ),
    [selectedType],
  )

  const isAllergenSelectShown = useMemo(
    () => selectedType === UNKNOWN_WORD_TYPE.ALLERGEN_LABEL.value,
    [selectedType],
  )

  const isDietSelectShown = useMemo(
    () => selectedType === UNKNOWN_WORD_TYPE.DIET_LABEL.value,
    [selectedType],
  )

  const isConfirmed = useMemo(
    () => unknownWord.isCurationDone && unknownWord.isQaDone,
    [unknownWord.isCurationDone, unknownWord.isQaDone],
  )

  const isUnconfirmButtonShown = useMemo(
    () =>
      !isDirty &&
      !unknownWord.isExported &&
      ((unknownWord.isCurationDone && !isUnknownWordsCurationDone) ||
        (unknownWord.isQaDone && !isUnknownWordsQaDone)),
    [
      isDirty,
      isUnknownWordsCurationDone,
      isUnknownWordsQaDone,
      unknownWord.isCurationDone,
      unknownWord.isExported,
      unknownWord.isQaDone,
    ],
  )

  const isCancelButtonShown = useMemo(
    () => isDirty && !isConfirmed,
    [isDirty, isConfirmed],
  )

  const isFormDisabled = useMemo(
    () => isDisabled || isConfirmed,
    [isDisabled, isConfirmed],
  )

  const isContextDependantFlagDisabled = useMemo(
    () => isFormDisabled || unknownWord.isContextDependantFromAi,
    [isFormDisabled, unknownWord.isContextDependantFromAi],
  )

  const confirmButtonText = useMemo(() => {
    if (unknownWord.isExported) {
      return 'Exported'
    }

    if (isConfirmed) {
      return 'Confirmed'
    }

    if (!unknownWord.isCurationDone) {
      return 'Confirm Curation'
    }

    return 'Confirm QA'
  }, [isConfirmed, unknownWord.isCurationDone, unknownWord.isExported])

  const handleReset = useCallback(() => resetForm(), [resetForm])

  const onSubmit = useCallback(() => {
    form.submit()
  }, [form])

  const handleFormValuesChange = useCallback(changedValues => {
    if (!isNil(changedValues.type)) {
      setSelectedType(changedValues.type)
    }
  }, [])

  return (
    <Form
      form={form}
      initialValues={initialValues}
      name="editUnknownWord"
      onFinish={onConfirm}
      autoComplete="off"
      onFieldsChange={setToDirty}
      onValuesChange={handleFormValuesChange}
    >
      <FormRichTextHighlightingInput
        form={form}
        name="context"
        isDisabled={isFormDisabled}
        toolbarItems={['bold']}
        onlySelection
        maxEditorHeight={90}
      />

      {!isEmpty(translation) && !isEmpty(initialLanguage) && (
        <StyledUnknownWordsTranslationText>
          ({initialLanguage.name} - {translation.text})
        </StyledUnknownWordsTranslationText>
      )}
      <StyledUnknownWordsFormSelect
        showSearch
        disabled={isFormDisabled}
        name="type"
        size="small"
        options={unknownWordTypeOptions}
      />
      <FormCheckbox
        label="Context dependant"
        name="isContextDependant"
        disabled={isContextDependantFlagDisabled}
      />
      {isAllergenSelectShown && (
        <StyledUnknownWordsFormSelect
          showSearch
          disabled={isFormDisabled}
          name="allergenId"
          size="small"
          options={allergenOptions}
          labelAbove
          smallLabel
          label={
            labelLegendMatchType && (
              <Typography.Text>
                Allergen Label/{' '}
                <StyledUnknownWordsMatchTypeText>
                  {LABEL_LEGEND_MATCH_TYPE[labelLegendMatchType]}
                </StyledUnknownWordsMatchTypeText>
              </Typography.Text>
            )
          }
        />
      )}
      {isDietSelectShown && (
        <StyledUnknownWordsFormSelect
          showSearch
          disabled={isFormDisabled}
          name="dietId"
          size="small"
          options={dietOptions}
          labelAbove
          smallLabel
          label={
            labelLegendMatchType && (
              <Typography.Text>
                Diet Label/{' '}
                <StyledUnknownWordsMatchTypeText>
                  {LABEL_LEGEND_MATCH_TYPE[labelLegendMatchType]}
                </StyledUnknownWordsMatchTypeText>
              </Typography.Text>
            )
          }
        />
      )}
      {isIngredientSelectsShown && (
        <StyledUnknownWordsFormTreeSelect
          showSearch
          disabled={isFormDisabled}
          name="ingredientId"
          treeData={ingredientOptions}
          size="small"
        />
      )}
      {isIngredientMatchTypeShown && (
        <StyledUnknownWordsFormSelect
          disabled={isFormDisabled}
          showSearch
          name="ingredientKeywordMatchType"
          size="small"
          options={ingredientKeywordMatchTypeOptions}
        />
      )}
      {isLanguageSelectShown && (
        <StyledUnknownWordsFormSelect
          defaultValue={initialLanguage?.id}
          showSearch
          disabled={isFormDisabled}
          name="languageId"
          size="small"
          options={languageOptions}
          placeholder="Select a language..."
        />
      )}
      <StyledVerticalSpace size="middle">
        <Space wrap>
          <Popconfirm
            disabled={isFormDisabled}
            onConfirm={onSubmit}
            title="This will confirm the unknown word. Are you sure?"
          >
            <Button disabled={isFormDisabled} htmlType="submit">
              {confirmButtonText}
            </Button>
          </Popconfirm>
          {isCancelButtonShown && <Button onClick={handleReset}>Cancel</Button>}
          {isUnconfirmButtonShown && (
            <Popconfirm
              onConfirm={onUnconfirm}
              title="This will unconfirm the unknown word. Are you sure?"
            >
              <Button>Unconfirm</Button>
            </Popconfirm>
          )}
        </Space>

        {hasPermissionToDelete && (
          <Button
            disabled={isFormDisabled}
            onClick={onDelete}
            icon={<DeleteOutlined />}
          >
            Delete word
          </Button>
        )}
      </StyledVerticalSpace>
    </Form>
  )
}

UnknownWordForm.propTypes = {
  unknownWord: unknownWordPropType.isRequired,
  onDelete: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onUnconfirm: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  isUnknownWordsCurationDone: PropTypes.bool,
  isUnknownWordsQaDone: PropTypes.bool,
}

UnknownWordForm.defaultProps = {
  isDisabled: false,
  isUnknownWordsCurationDone: false,
  isUnknownWordsQaDone: false,
}

export default UnknownWordForm
